import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api";
import { formatAbsolute } from "../../lib/datetime";
import { deletePersona, PERSONA_STATE_TRANSITIONS, type PersonaState, type CoverIdentity } from "../../api/personas";
import { listModels, modelIsSelectable } from "../../api/models";
import {
  usePersona,
  useTransitionPersona,
  useUpdatePersona,
} from "../../hooks/usePersona";
import { Card } from "../../components/Card";
import { StateBadge } from "../../components/StateBadge";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ConfirmModal } from "../../components/ConfirmModal";
import { PersonaStateMachineHeader } from "./StateMachineHeader";
import { VoiceRefCard } from "./VoiceRefCard";
import { ConsentCard } from "./ConsentCard";
import { ApplyTemplateModal } from "./ApplyTemplateModal";
import type { StateBadgeKind } from "../../components/StateBadge";

// ─── Helpers ─────────────────────────────────────────────────────────────────────

function stateToBadgeKind(state: PersonaState): StateBadgeKind {
  switch (state) {
    case "active":  return "active";
    case "draft":   return "draft";
    case "retired": return "retired";
  }
}

function stateLabel(state: PersonaState): string {
  const labels: Record<PersonaState, string> = {
    draft: "DRAFT",
    active: "ACTIVE",
    retired: "RETIRED",
  };
  return labels[state];
}

function transitionLabel(target: PersonaState): string {
  const labels: Record<PersonaState, string> = {
    draft: "← Back to Draft",
    active: "→ Activate",
    retired: "→ Retire",
  };
  return labels[target];
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return formatAbsolute(iso);
}

// ─── Initials avatar ──────────────────────────────────────────────────────────────

function initialsAvatar(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

// ─── Read-only field display ──────────────────────────────────────────────────────


// ─── Cover identity ──────────────────────────────────────────────────────────

const COVER_FIELDS: { key: keyof CoverIdentity; label: string; placeholder: string }[] = [
  { key: "job_title", label: "Job title", placeholder: "e.g. IT Help Desk Analyst" },
  { key: "department", label: "Department / team", placeholder: "e.g. Service Desk" },
  { key: "manager_name", label: "Manager name", placeholder: "e.g. Dana Whitfield" },
  { key: "manager_title", label: "Manager title", placeholder: "e.g. Service Desk Lead" },
  { key: "office_location", label: "Office / location", placeholder: "e.g. Remote (Austin)" },
  { key: "employee_id", label: "Employee / badge ID", placeholder: "e.g. E-40318" },
  { key: "tenure", label: "Tenure", placeholder: "e.g. about three years" },
  { key: "callback_number", label: "Callback number", placeholder: "e.g. +1 512 555 0148" },
  { key: "monitoring_tool", label: "Monitoring / ticketing tool", placeholder: "e.g. CrowdStrike Falcon" },
  { key: "ticket_reference", label: "Ticket / case reference", placeholder: "e.g. INC0042317" },
];

/**
 * One draft object committed on blur, rather than ten pieces of state.
 *
 * These are the answers to identity challenges. A real employee knows their own
 * job title and who they report to; on call c5857fea the agent had none of this
 * and answered "I'm not sure" / "I don't have a name handy" / "I don't have one
 * on file here", and those four deflections ended the pretext -- not the
 * credential request.
 */
function CoverIdentityCard({
  rec,
  onCommit,
}: {
  rec: { cover_identity?: CoverIdentity | null };
  onCommit: (ci: CoverIdentity) => void;
}) {
  const current = rec.cover_identity ?? {};
  const [draft, setDraft] = useState<Record<string, string>>({});

  const valueFor = (k: string) =>
    draft[k] ?? ((current as Record<string, unknown>)[k] as string | null | undefined) ?? "";

  const commit = () => {
    if (Object.keys(draft).length === 0) return;
    const merged: Record<string, unknown> = {};
    for (const f of COVER_FIELDS) {
      const v = valueFor(f.key as string).trim();
      merged[f.key as string] = v === "" ? null : v;
    }
    setDraft({});
    onCommit(merged as CoverIdentity);
  };

  const filled = COVER_FIELDS.filter((f) => valueFor(f.key as string).trim() !== "").length;

  return (
    <Card title="Cover identity">
      <div style={{ fontSize: 10, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 4 }}>
        Facts the agent may state about itself. Anything left blank, it will say it
        does not have — so an empty field is a deflection on a live call.
        {filled > 0 ? ` ${filled} of ${COVER_FIELDS.length} supplied.` : " None supplied yet."}
      </div>
      {COVER_FIELDS.map((f) => (
        <div key={f.key as string} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <FieldLabel>{f.label}</FieldLabel>
          <input
            value={valueFor(f.key as string)}
            placeholder={f.placeholder}
            onChange={(e) => setDraft((d) => ({ ...d, [f.key as string]: e.target.value }))}
            onBlur={commit}
            style={{
              background: "var(--bg-input)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "7px 10px",
              color: "var(--text-primary)",
              fontSize: 12,
              fontFamily: "var(--font-sans)",
              outline: "none",
            }}
          />
        </div>
      ))}
    </Card>
  );
}

function FieldLabel({ children }: { children: string }) {
  return (
    <label
      style={{
        fontSize: 9.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: "var(--text-muted)",
        fontWeight: 600,
      }}
    >
      {children}
    </label>
  );
}

function ReadOnlyField({
  value,
  mono = false,
}: {
  value: string;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        fontFamily: mono ? "var(--font-mono)" : undefined,
        fontSize: mono ? 11 : 12,
        color: "var(--text-muted)",
        background: "var(--bg-input)",
        borderRadius: 8,
        padding: "7px 10px",
        border: "1px solid var(--border)",
        wordBreak: "break-all",
      }}
    >
      {value}
    </div>
  );
}

function StatRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: 10.5,
        padding: "3px 0",
        gap: 8,
      }}
    >
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span
        style={{
          fontFamily: mono ? "var(--font-mono)" : undefined,
          color: "var(--text-primary)",
          fontWeight: 600,
          textAlign: "right",
          wordBreak: "break-all",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Detail page ─────────────────────────────────────────────────────────────────

export function PersonaDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, isError } = usePersona(id ?? "");
  const updateMutation = useUpdatePersona(id ?? "");
  const transitionMutation = useTransitionPersona(id ?? "");
  const { data: models = [] } = useQuery({
    queryKey: ["models"],
    queryFn: listModels,
  });

  // Delete state
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteMut = useMutation({
    mutationFn: () => deletePersona(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["personas"] });
      navigate("/personas");
    },
    onError: (err) =>
      alert(
        err instanceof ApiError && err.status === 409
          ? "This persona is in use by an active engagement and can't be deleted."
          : err instanceof ApiError
            ? err.message
            : "Failed to delete persona.",
      ),
  });

  // Editable field state
  const [editName, setEditName] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<string | null>(null);
  const [editBio, setEditBio] = useState<string | null>(null);

  // Transition modal state
  const [transitionTarget, setTransitionTarget] = useState<PersonaState | null>(null);
  const [transitionComment, setTransitionComment] = useState("");
  const [transitionError, setTransitionError] = useState<string | null>(null);

  // Apply template modal state
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);

  if (!id) {
    return <div style={{ padding: 24, color: "var(--error)" }}>Missing persona ID.</div>;
  }

  if (isLoading) {
    return (
      <div style={{ padding: 24, color: "var(--text-muted)", fontFamily: "var(--font-sans)" }}>
        Loading…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={{ padding: 24, color: "var(--error)", fontFamily: "var(--font-sans)" }}>
        Failed to load persona.{" "}
        <button
          onClick={() => navigate("/personas")}
          style={{ color: "var(--accent)", background: "none", border: "none", cursor: "pointer" }}
        >
          ← Back
        </button>
      </div>
    );
  }

  const rec = data.record;
  const validTransitions = PERSONA_STATE_TRANSITIONS[rec.state];

  // ── Save-on-blur handlers ──────────────────────────────────────────────────────

  function handleNameBlur() {
    const trimmed = (editName ?? "").trim();
    if (trimmed && trimmed !== rec.display_name) {
      updateMutation.mutate({ display_name: trimmed });
    }
    setEditName(null);
  }

  function handleRoleBlur() {
    const trimmed = (editRole ?? "").trim();
    if (trimmed && trimmed !== rec.role) {
      updateMutation.mutate({ role: trimmed });
    }
    setEditRole(null);
  }

  function handleBioBlur() {
    const trimmed = (editBio ?? "").trim();
    const current = rec.bio ?? "";
    if (trimmed !== current) {
      updateMutation.mutate({ bio: trimmed });
    }
    setEditBio(null);
  }

  // ── Transition handlers ────────────────────────────────────────────────────────

  function openTransition(target: PersonaState) {
    setTransitionTarget(target);
    setTransitionComment("");
    setTransitionError(null);
  }

  function closeTransition() {
    setTransitionTarget(null);
    setTransitionComment("");
    setTransitionError(null);
    transitionMutation.reset();
  }

  async function confirmTransition() {
    if (!transitionTarget) return;
    setTransitionError(null);
    try {
      await transitionMutation.mutateAsync({
        target_state: transitionTarget,
        comment: transitionComment || undefined,
      });
      closeTransition();
    } catch (err) {
      if (err instanceof ApiError) {
        setTransitionError(`Server error ${err.status}: ${err.detail}`);
      } else {
        setTransitionError("Transition failed — unknown error");
      }
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────────

  const initials = initialsAvatar(rec.display_name);

  return (
    <div style={{ fontFamily: "var(--font-sans)", maxWidth: 1100 }}>
      {/* Breadcrumb */}
      <div
        style={{
          fontSize: 11,
          color: "var(--text-muted)",
          fontFamily: "var(--font-mono)",
          marginBottom: 12,
        }}
      >
        <Link
          to="/personas"
          style={{ color: "var(--text-muted)", textDecoration: "none" }}
        >
          Personas
        </Link>
        <span style={{ padding: "0 6px" }}>/</span>
        <span style={{ color: "var(--text-primary)", fontWeight: 700 }}>
          {rec.display_name}
        </span>
      </div>

      {/* ── Hero header card ── */}
      <div
        style={{
          background: "var(--bg-card-hero)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(59,130,246,0.22)",
          borderRadius: 12,
          padding: "14px 16px",
          position: "relative",
          overflow: "hidden",
          marginBottom: 14,
        }}
      >
        {/* Top shimmer line */}
        <div
          style={{
            position: "absolute",
            inset: "0 0 auto",
            height: 1,
            background:
              "linear-gradient(90deg, transparent, rgba(59,130,246,0.4), transparent)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          {/* Avatar + name + state */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {/* Initials avatar */}
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "var(--accent)",
                  color: "white",
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 700,
                  fontSize: 13,
                  flexShrink: 0,
                  letterSpacing: 0.5,
                }}
              >
                {initials || "?"}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: "var(--text-primary)",
                    }}
                  >
                    {rec.display_name}
                  </span>
                  <StateBadge kind={stateToBadgeKind(rec.state)}>
                    {stateLabel(rec.state)}
                  </StateBadge>
                </div>
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {rec.role}
                </span>
              </div>
            </div>

            {/* State machine visualization */}
            <PersonaStateMachineHeader current={rec.state} />
          </div>

          {/* Transition buttons + delete */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {validTransitions.map((target) => {
              const isDestructive = target === "retired";
              return (
                <button
                  key={target}
                  onClick={() => openTransition(target)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 6,
                    fontSize: 10.5,
                    fontWeight: 600,
                    border: isDestructive
                      ? "1px solid rgba(239,68,68,0.5)"
                      : "1px solid var(--accent)",
                    background: isDestructive
                      ? "rgba(239,68,68,0.12)"
                      : "var(--accent)",
                    color: isDestructive ? "var(--error)" : "white",
                    cursor: "pointer",
                  }}
                >
                  {transitionLabel(target)}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              style={{
                padding: "5px 12px",
                borderRadius: 6,
                fontSize: 10.5,
                fontWeight: 600,
                border: "1px solid var(--error)",
                background: "transparent",
                color: "var(--error)",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Delete persona
            </button>
          </div>
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 280px",
          gap: 14,
          alignItems: "start",
        }}
      >
        {/* ── Left column ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Identity card */}
          <Card title="Identity">
            {/* Display name — editable */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <FieldLabel>Display name</FieldLabel>
              <input
                value={editName ?? rec.display_name}
                onChange={(e) => setEditName(e.target.value)}
                onFocus={() => setEditName(rec.display_name)}
                onBlur={handleNameBlur}
                style={{
                  background: "var(--bg-input)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "7px 10px",
                  color: "var(--text-primary)",
                  fontSize: 12,
                  fontFamily: "var(--font-sans)",
                  outline: "none",
                }}
              />
            </div>

            {/* Role — editable */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <FieldLabel>Role</FieldLabel>
              <input
                value={editRole ?? rec.role}
                onChange={(e) => setEditRole(e.target.value)}
                onFocus={() => setEditRole(rec.role)}
                onBlur={handleRoleBlur}
                style={{
                  background: "var(--bg-input)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "7px 10px",
                  color: "var(--text-primary)",
                  fontSize: 12,
                  fontFamily: "var(--font-sans)",
                  outline: "none",
                }}
              />
            </div>

            {/* Bio — editable textarea */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <FieldLabel>Bio</FieldLabel>
              <textarea
                value={editBio ?? (rec.bio ?? "")}
                onChange={(e) => setEditBio(e.target.value)}
                onFocus={() => setEditBio(rec.bio ?? "")}
                onBlur={handleBioBlur}
                rows={4}
                placeholder="A short description of this persona…"
                style={{
                  background: "var(--bg-input)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "7px 10px",
                  color: "var(--text-primary)",
                  fontSize: 12,
                  fontFamily: "var(--font-sans)",
                  outline: "none",
                  resize: "vertical",
                }}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {/* Created at — read-only */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <FieldLabel>Created</FieldLabel>
                <ReadOnlyField value={formatDate(rec.created_at)} />
              </div>

              {/* Created by — read-only */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <FieldLabel>Created by</FieldLabel>
                <ReadOnlyField value={rec.created_by} />
              </div>
            </div>

            {/* Persona ID — read-only mono */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <FieldLabel>Persona ID</FieldLabel>
              <ReadOnlyField value={rec.persona_id} mono />
            </div>
          </Card>


          {/* Cover identity — the facts the agent may state about itself.
              Without these the agent deflects on every identity challenge
              ("I'm not sure", "I don't have a name handy"), which is what
              burned the pretext on call c5857fea. */}
          <CoverIdentityCard rec={rec} onCommit={(ci) => updateMutation.mutate({ cover_identity: ci })} />

          {/* Read-only channel display (v1 — no editing) */}
          {(rec.from_email_address || rec.voice_clone_provider || rec.video_avatar_provider) && (
            <Card title="Channel wiring (read-only)" accentColor="var(--text-muted)">
              <div style={{ fontSize: 10, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 4 }}>
                Channel wiring is CLI-only in v1. These fields are read-only.
              </div>
              {rec.from_email_address && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <FieldLabel>From email</FieldLabel>
                  <ReadOnlyField value={rec.from_email_address} mono />
                </div>
              )}
              {rec.voice_clone_provider && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <FieldLabel>Voice provider</FieldLabel>
                  <ReadOnlyField value={rec.voice_clone_provider} />
                </div>
              )}
              {rec.video_avatar_provider && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <FieldLabel>Video avatar provider</FieldLabel>
                  <ReadOnlyField value={rec.video_avatar_provider} />
                </div>
              )}
            </Card>
          )}

          {/* Synthetic toggle + Caller ID + System prompt */}
          <Card title="Configuration">
            {/* Synthetic toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="checkbox"
                checked={rec.synthetic}
                onChange={(e) => updateMutation.mutate({ synthetic: e.target.checked })}
                style={{ accentColor: "var(--accent)", width: 16, height: 16, cursor: "pointer" }}
              />
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>
                  Synthetic persona
                </div>
                <div style={{ fontSize: 10, color: "var(--text-muted)" }}>
                  Not based on a real person — consent record not required for activation.
                </div>
              </div>
            </div>

            {/* Caller ID DID */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <FieldLabel>Caller ID (E.164)</FieldLabel>
              <input
                defaultValue={rec.caller_id_did ?? ""}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (rec.caller_id_did ?? "")) {
                    updateMutation.mutate({ caller_id_did: v || null });
                  }
                }}
                placeholder="+12133290033"
                style={{
                  background: "var(--bg-input)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "7px 10px",
                  color: "var(--text-primary)",
                  fontSize: 12,
                  fontFamily: "var(--font-mono)",
                  outline: "none",
                }}
              />
            </div>

            {/* Language model */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <FieldLabel>Language model</FieldLabel>
              <select
                value={rec.llm_model ?? ""}
                onChange={(e) =>
                  updateMutation.mutate({ llm_model: e.target.value || null })
                }
                style={{
                  background: "var(--bg-input)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "7px 10px",
                  color: "var(--text-primary)",
                  fontSize: 12,
                  fontFamily: "var(--font-mono)",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                <option value="">Default (Mistral Large 3)</option>
                {models.map((m) => (
                  <option key={m.key} value={m.key} disabled={!modelIsSelectable(m)}>
                    {m.label}
                    {modelIsSelectable(m) ? "" : " — set its API key in Settings"}
                  </option>
                ))}
              </select>
            </div>

            {/* System prompt override */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <FieldLabel>System prompt override</FieldLabel>
                <button
                  onClick={() => setShowApplyTemplate(true)}
                  style={{
                    padding: "3px 10px",
                    borderRadius: 6,
                    fontSize: 10,
                    fontWeight: 600,
                    border: "1px solid var(--accent)",
                    background: "transparent",
                    color: "var(--accent)",
                    cursor: "pointer",
                  }}
                >
                  Apply template
                </button>
              </div>
              <textarea
                key={rec.system_prompt_override ?? ""}
                defaultValue={rec.system_prompt_override ?? ""}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (rec.system_prompt_override ?? "")) {
                    updateMutation.mutate({ system_prompt_override: v || null });
                  }
                }}
                rows={6}
                placeholder="Custom system prompt for this persona..."
                style={{
                  background: "var(--bg-input)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "7px 10px",
                  color: "var(--text-primary)",
                  fontSize: 12,
                  fontFamily: "var(--font-sans)",
                  outline: "none",
                  resize: "vertical",
                }}
              />
            </div>

            {/* Consent (only for non-synthetic) */}
            {!rec.synthetic && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <FieldLabel>Consent record URI</FieldLabel>
                <input
                  defaultValue={rec.consent_record_uri ?? ""}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (rec.consent_record_uri ?? "")) {
                      updateMutation.mutate({ consent_record_uri: v || null });
                    }
                  }}
                  placeholder="s3://anglerfish-consent/signed-consent.pdf"
                  style={{
                    background: "var(--bg-input)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "7px 10px",
                    color: "var(--text-primary)",
                    fontSize: 12,
                    fontFamily: "var(--font-mono)",
                    outline: "none",
                  }}
                />
              </div>
            )}
          </Card>

          {/* Voice ref card */}
          <VoiceRefCard
            record={rec}
            onUpdate={(fields) => updateMutation.mutate(fields)}
          />

          {/* Consent card */}
          <ConsentCard record={rec} />
        </div>

        {/* ── Right rail ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

          {/* Usage placeholder */}
          <Card title="Usage" accentColor="var(--text-muted)">
            <div
              style={{
                fontSize: 10.5,
                color: "var(--text-muted)",
                lineHeight: 1.6,
                fontStyle: "italic",
              }}
            >
              Call data not yet wired. Active call count and engagement bindings
              will appear here when telemetry lands.
            </div>
          </Card>

          {/* Lifecycle snapshot */}
          <Card title="Lifecycle" accentColor="var(--success)">
            <StatRow label="State" value={stateLabel(rec.state)} />
            <StatRow label="Created" value={formatDate(rec.created_at)} />
            {rec.retired_at && (
              <StatRow label="Retired" value={formatDate(rec.retired_at)} />
            )}
            {rec.retired_by && (
              <StatRow label="Retired by" value={rec.retired_by} />
            )}
          </Card>

          {/* Retirement explainer */}
          {rec.state !== "retired" && (
            <Card title="Retirement" accentColor="var(--error)">
              <div
                style={{
                  fontSize: 10.5,
                  color: "var(--text-muted)",
                  lineHeight: 1.6,
                }}
              >
                Retirement is one-way. The persona becomes immutable and can no
                longer be bound to new engagements.
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* ── Transition confirm modal ── */}
      <ConfirmModal
        open={transitionTarget !== null}
        title={`Transition to ${transitionTarget ? stateLabel(transitionTarget) : ""}`}
        description={`This will move the persona from ${stateLabel(rec.state)} to ${transitionTarget ? stateLabel(transitionTarget) : ""}. ${transitionTarget === "retired" ? "Retirement is one-way and cannot be undone." : "This action may be irreversible."}`}
        confirmLabel={transitionTarget ? transitionLabel(transitionTarget) : "Confirm"}
        danger={transitionTarget === "retired"}
        withComment
        comment={transitionComment}
        onCommentChange={setTransitionComment}
        loading={transitionMutation.isPending}
        error={transitionError}
        onConfirm={() => { void confirmTransition(); }}
        onCancel={closeTransition}
      />

      {/* ── Apply template modal ── */}
      <ApplyTemplateModal
        open={showApplyTemplate}
        personaName={rec.display_name}
        onApply={({ system_prompt_override, role, language }) => {
          updateMutation.mutate({ system_prompt_override, role, language });
        }}
        onClose={() => setShowApplyTemplate(false)}
      />

      {/* ── Delete persona confirm ── */}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete persona?"
        body="Past calls keep their snapshotted copy of this persona. This cannot be undone."
        busy={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate()}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
